import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';

const ThemeContext = createContext(null);

// Theme configurations
export const themes = {
  light: {
    mode: 'light',
    colors: {
      // Primary colors
      primary: {
        main: '#800020',
        light: '#A0153E',
        dark: '#5D0017',
        contrast: '#FFFFFF'
      },
      // Secondary colors
      secondary: {
        main: '#1A1A1A',
        light: '#2D2D2D',
        dark: '#000000',
        contrast: '#FFFFFF'
      },
      // Background colors
      background: {
        main: '#FFFFFF',
        default: '#FFFFFF',
        paper: '#F8F8F8',
        card: '#FFFFFF',
        elevated: '#FFFFFF',
        sidebar: '#2D2D2D'
      },
      // Text colors
      text: {
        primary: '#1A1A1A',
        secondary: '#666666',
        tertiary: '#999999',
        disabled: '#CCCCCC',
        white: '#FFFFFF',
        inverse: '#FFFFFF'
      },
      // Status colors
      success: {
        main: '#28A745',
        light: '#48C765',
        dark: '#1E7E34',
        bg: 'rgba(40, 167, 69, 0.1)'
      },
      error: {
        main: '#DC3545',
        light: '#E4606D',
        dark: '#BD2130',
        bg: 'rgba(220, 53, 69, 0.1)'
      },
      warning: {
        main: '#FFC107',
        light: '#FFD54F',
        dark: '#FFA000',
        bg: 'rgba(255, 193, 7, 0.1)'
      },
      info: {
        main: '#17A2B8',
        light: '#3FC3D8',
        dark: '#117A8B',
        bg: 'rgba(23, 162, 184, 0.1)'
      },
      // Border colors
      border: {
        light: '#E0E0E0',
        main: '#CCCCCC',
        dark: '#999999'
      },
      // Chart colors
      chart: {
        primary: '#800020',
        secondary: '#A0153E',
        tertiary: '#5D0017',
        accent1: '#C4A484',
        accent2: '#8B7355',
        accent3: '#D4AF37',
        gradient1: 'linear-gradient(135deg, #800020 0%, #A0153E 100%)',
        gradient2: 'linear-gradient(135deg, #1A1A1A 0%, #2D2D2D 100%)'
      },
      // Neutral colors
      neutral: {
        50: '#F8F9FA',
        100: '#F1F3F5',
        200: '#E9ECEF',
        300: '#DEE2E6',
        400: '#CED4DA',
        500: '#ADB5BD',
        600: '#6C757D',
        700: '#495057',
        800: '#343A40',
        900: '#212529'
      }
    },
    shadows: {
      sm: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
      md: '0 3px 6px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.12)',
      lg: '0 10px 20px rgba(0,0,0,0.15), 0 3px 6px rgba(0,0,0,0.10)',
      xl: '0 15px 25px rgba(0,0,0,0.15), 0 5px 10px rgba(0,0,0,0.05)',
      glow: '0 0 20px rgba(128, 0, 32, 0.3)'
    },
    glass: {
      background: 'rgba(255, 255, 255, 0.7)',
      border: '1px solid rgba(255, 255, 255, 0.3)',
      blur: 'blur(10px)'
    }
  },
  dark: {
    mode: 'dark',
    colors: {
      // Primary colors (adjusted for dark mode)
      primary: {
        main: '#C4314B',
        light: '#E05A73',
        dark: '#800020',
        contrast: '#FFFFFF'
      },
      // Secondary colors
      secondary: {
        main: '#2D2D2D',
        light: '#404040',
        dark: '#1A1A1A',
        contrast: '#FFFFFF'
      },
      // Background colors (dark)
      background: {
        main: '#121212',
        default: '#121212',
        paper: '#1E1E1E',
        card: '#252525',
        elevated: '#2D2D2D',
        sidebar: '#0D0D0D'
      },
      // Text colors (inverted)
      text: {
        primary: '#F5F5F5',
        secondary: '#B3B3B3',
        tertiary: '#808080',
        disabled: '#4D4D4D',
        white: '#FFFFFF',
        inverse: '#1A1A1A'
      },
      // Status colors (slightly brighter for dark mode)
      success: {
        main: '#4CAF50',
        light: '#81C784',
        dark: '#388E3C',
        bg: 'rgba(76, 175, 80, 0.15)'
      },
      error: {
        main: '#F44336',
        light: '#E57373',
        dark: '#D32F2F',
        bg: 'rgba(244, 67, 54, 0.15)'
      },
      warning: {
        main: '#FFB300',
        light: '#FFCA28',
        dark: '#FF8F00',
        bg: 'rgba(255, 179, 0, 0.15)'
      },
      info: {
        main: '#29B6F6',
        light: '#4FC3F7',
        dark: '#0288D1',
        bg: 'rgba(41, 182, 246, 0.15)'
      },
      // Border colors (darker)
      border: {
        light: '#333333',
        main: '#444444',
        dark: '#555555'
      },
      // Chart colors (brighter for dark backgrounds)
      chart: {
        primary: '#C4314B',
        secondary: '#E05A73',
        tertiary: '#FF8A9A',
        accent1: '#D4B896',
        accent2: '#A08B70',
        accent3: '#FFD700',
        gradient1: 'linear-gradient(135deg, #800020 0%, #C4314B 100%)',
        gradient2: 'linear-gradient(135deg, #2D2D2D 0%, #404040 100%)'
      },
      // Neutral colors (inverted)
      neutral: {
        50: '#212529',
        100: '#343A40',
        200: '#495057',
        300: '#6C757D',
        400: '#ADB5BD',
        500: '#CED4DA',
        600: '#DEE2E6',
        700: '#E9ECEF',
        800: '#F1F3F5',
        900: '#F8F9FA'
      }
    },
    shadows: {
      sm: '0 1px 3px rgba(0,0,0,0.4), 0 1px 2px rgba(0,0,0,0.5)',
      md: '0 3px 6px rgba(0,0,0,0.45), 0 2px 4px rgba(0,0,0,0.4)',
      lg: '0 10px 20px rgba(0,0,0,0.5), 0 3px 6px rgba(0,0,0,0.4)',
      xl: '0 15px 25px rgba(0,0,0,0.5), 0 5px 10px rgba(0,0,0,0.35)',
      glow: '0 0 20px rgba(196, 49, 75, 0.4)'
    },
    glass: {
      background: 'rgba(30, 30, 30, 0.7)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      blur: 'blur(10px)'
    }
  }
};

export const ThemeProvider = ({ children }) => {
  // Initialize from localStorage or system preference
  const [themeMode, setThemeMode] = useState(() => {
    const saved = localStorage.getItem('themeMode');
    if (saved) return saved;
    return 'light';
  });

  const theme = useMemo(() => themes[themeMode], [themeMode]);

  // Toggle theme
  const toggleTheme = useCallback(() => {
    setThemeMode(prev => prev === 'light' ? 'dark' : 'light');
  }, []);

  // Set specific theme
  const setTheme = useCallback((mode) => {
    if (mode === 'light' || mode === 'dark') {
      setThemeMode(mode);
    }
  }, []);

  // Persist theme preference
  useEffect(() => {
    localStorage.setItem('themeMode', themeMode);

    // Update CSS variables on document root
    const root = document.documentElement;
    root.setAttribute('data-theme', themeMode);

    // Set CSS custom properties
    Object.entries(theme.colors).forEach(([category, values]) => {
      if (typeof values === 'object') {
        Object.entries(values).forEach(([key, value]) => {
          root.style.setProperty(`--color-${category}-${key}`, value);
        });
      }
    });

    Object.entries(theme.shadows).forEach(([key, value]) => {
      root.style.setProperty(`--shadow-${key}`, value);
    });
  }, [themeMode, theme]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e) => {
      // Only auto-switch if user hasn't explicitly set a preference
      const userPreference = localStorage.getItem('themeMode');
      if (!userPreference) {
        setThemeMode(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const value = useMemo(() => ({
    theme,
    themeMode,
    isDark: themeMode === 'dark',
    toggleTheme,
    setTheme
  }), [theme, themeMode, toggleTheme, setTheme]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
